import { defineStore } from 'pinia';
import type { Partner } from '../types/models';
import { identityService } from '../services/identity.service';
import { endSession, type SessionEndReason } from '../services/session-journal.service';

export interface AuthState {
  isLoggedIn: boolean;
  userType: 'employee' | 'contractor' | null;

  // Employee data
  employeeId: string | null;
  employeeIdent: string | null;
  employeeName: string | null;
  employeeEmail: string | null;
  employeeRole: string | null;
  employeeSolicitor: string | null;
  allowedProcedures: string[]; // Lista numerów procedur z tagu <kis>
  isPrivileged: boolean; // true jeśli allowedProcedures jest pusty (pełne uprawnienia)

  // Contractor data
  partnerId: string | null;
  partnerIdent: string | null;
  partnerName: string | null;
  partnerNip: string | null;

  // For employee: selected customer for order
  selectedPartner: Partner | null;

  loginTimestamp: number;

  // Wpis sesji w dzienniku (Journal, sv_session z 2647); null = brak dziennika
  // (SSO z pulpitu, backend bez tej funkcji)
  sessionId: string | null;
}

const STORAGE_KEY = 'firmatic-auth';

// Load initial state from localStorage
function loadStateFromStorage(): Partial<AuthState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load auth state:', e);
  }
  return {};
}

/** Ścieżka whoami instalacji jednoinstancyjnej (brama serwuje /api/whoami na swoim origin). */
export const DEFAULT_WHOAMI_PATH = '/api/whoami';

/**
 * Tylko ścieżka same-origin, w przeciwnym razie domyślna.
 *
 * Ścieżkę seeduje pulpit w localStorage (klucz `firmatic-sso-whoami`), bo jeden
 * origin potrafi obsługiwać DWIE instancje Firmatic i `/api/*` nie jest między
 * nie rozdzielane — patrz `loginWithSsoToken`. localStorage jest zapisywalny
 * przez dowolny skrypt tego originu, a w żądaniu niesiemy TOKEN SESYJNY BRAMY:
 * adres absolutny („https://obcy/", „//obcy/", „/\obcy/") wysłałby ten token na
 * cudzy host. Stąd wąska biała lista znaków ścieżki — whoami nie potrzebuje ani
 * query, ani fragmentu, bo token idzie nagłówkiem.
 */
const SAFE_WHOAMI_PATH = /^\/(?!\/)[A-Za-z0-9._~\-/]*$/;

export function resolveWhoamiUrl(seed?: string | null): string {
  const path = (seed ?? '').trim();
  if (!SAFE_WHOAMI_PATH.test(path)) return DEFAULT_WHOAMI_PATH;
  return path;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isLoggedIn: false,
    userType: null,

    employeeId: null,
    employeeIdent: null,
    employeeName: null,
    employeeEmail: null,
    employeeRole: null,
    employeeSolicitor: null,
    allowedProcedures: [],
    isPrivileged: false,

    partnerId: null,
    partnerIdent: null,
    partnerName: null,
    partnerNip: null,

    selectedPartner: null,

    loginTimestamp: 0,

    sessionId: null,

    ...loadStateFromStorage(), // Restore from localStorage
  }),

  getters: {
    isEmployee: (state) => state.userType === 'employee',
    isContractor: (state) => state.userType === 'contractor',
    isAdmin: (state) => state.employeeRole?.toLowerCase() === 'admin',
    canPlaceOrder: (state) =>
      state.userType === 'contractor' ||
      (state.userType === 'employee' && state.selectedPartner !== null),
    displayName: (state) =>
      state.userType === 'employee' ? state.employeeName : state.partnerName,
  },

  actions: {
    saveToStorage() {
      // Tożsamość TEJ karty wpychamy do migawki ZANIM dotkniemy localStorage:
      // klucz jest wspólny dla originu (dwie instancje Firmatic pod jednym
      // adresem), więc odczyt zwrotny mógłby oddać stan cudzej karty.
      identityService.set({ employeeId: this.employeeId, partnerId: this.partnerId });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.$state));
      } catch (e) {
        console.error('Failed to save auth state:', e);
      }
    },

    loginEmployee(
      userId: string,
      userName: string,
      role: string,
      allowedProcedures: string[] = [],
      isPrivileged: boolean = false,
      userEmail: string = '',
      userIdent: string = '',
      solicitorIdent: string = '',
      sessionId: string = ''
    ) {
      console.log('[Auth Store] loginEmployee called with:', {
        userId,
        userName,
        userIdent,
        userEmail,
        solicitorIdent,
        role,
        allowedProcedures,
        allowedProceduresLength: allowedProcedures.length,
        isPrivileged
      });

      this.isLoggedIn = true;
      this.userType = 'employee';
      this.employeeId = userId;
      this.employeeIdent = userIdent;
      this.employeeName = userName;
      this.employeeEmail = userEmail;
      this.employeeRole = role;
      this.employeeSolicitor = solicitorIdent || null;
      this.allowedProcedures = allowedProcedures;
      this.isPrivileged = isPrivileged;
      this.loginTimestamp = Date.now();
      this.sessionId = sessionId || null;

      // Clear selected partner from previous session
      // Each login session should start fresh without pre-selected partner
      this.selectedPartner = null;

      console.log('[Auth Store] State after loginEmployee:', {
        allowedProcedures: this.allowedProcedures,
        allowedProceduresLength: this.allowedProcedures.length,
        isPrivileged: this.isPrivileged,
        userName: this.employeeName,
        userEmail: this.employeeEmail
      });

      this.saveToStorage();
    },

    /**
     * SSO tokenem (osadzenie w pulpicie): waliduje token sesyjny bramy przez whoami
     * (same-origin) i loguje pracownika z ZWERYFIKOWANEJ tożsamości — zamiast ufać
     * seedowanemu blobowi firmatic-auth. 401 (wygasł/revoke=logout w pulpicie) -> wyloguj
     * (revocation wygrywa nad stanem z localStorage). Błąd sieci -> zostaw stan z seedu.
     *
     * `whoamiSeed` to ścieżka whoami TEJ instancji, zaseedowana przez pulpit obok tokenu.
     * Jeden origin potrafi obsługiwać dwie instancje Firmatic (ZKS: zks.firmatic.pl:9443
     * → brama preprod 2.99 i brama produkcji 2.100), a rozdzielane per instancja są tylko
     * WS bramy i BFF — `/api/*` leci catch-allem do PREPRODU. Tokeny bramy żyją w mapie
     * in-memory per brama, więc token wydany przez produkcję był tam nieznany: 401 →
     * logout() → guard routera → ekran logowania. Stąd „W nowej karcie" na produkcji
     * żądało logowania, a kafel w iframe działał (otwiera się tuż po zaseedowaniu).
     * Brak seedu (instalacja jednoinstancyjna, tryb standalone) → '/api/whoami' jak dotąd.
     */
    async loginWithSsoToken(token: string, whoamiSeed?: string | null): Promise<boolean> {
      try {
        const res = await fetch(resolveWhoamiUrl(whoamiSeed), { headers: { 'X-Firmatic-Token': token } });
        if (res.status === 401) {
          this.logout();
          return false;
        }
        if (!res.ok) return false;
        const w = await res.json(); // {type:'kis', kis:[], privileged, user:{id,ident,name,email,role,solicitor}}
        const u = w.user || {};
        this.loginEmployee(
          u.id || '',
          u.name || u.ident || '',
          u.role || '',
          Array.isArray(w.kis) ? w.kis : [],
          !!w.privileged,
          u.email || '',
          u.ident || '',
          u.solicitor || ''
        );
        return true;
      } catch (e) {
        // brak sieci/bramy -> NIE wylogowuj (zostaw stan z seedu firmatic-auth)
        console.warn('[Auth] SSO whoami nieudane, fallback do seedu:', e);
        return false;
      }
    },

    /**
     * Sprawdza czy użytkownik ma uprawnienia do wykonania procedury
     * @param procedureNumber - Numer procedury (np. '4005', '4010')
     * @returns true jeśli użytkownik ma uprawnienia (lub jest uprzywilejowany)
     */
    hasPermission(procedureNumber: string): boolean {
      if (this.userType !== 'employee') {
        return false; // Tylko dla pracowników
      }

      if (this.isPrivileged) {
        console.log(`[Auth] hasPermission(${procedureNumber}): true (user is privileged)`);
        return true; // Użytkownik uprzywilejowany - pełne uprawnienia
      }

      const hasAccess = this.allowedProcedures.includes(procedureNumber);
      console.log(`[Auth] hasPermission(${procedureNumber}):`, {
        hasAccess,
        allowedProcedures: this.allowedProcedures,
        userName: this.employeeName,
        isPrivileged: this.isPrivileged
      });
      return hasAccess;
    },

    loginContractor(partnerId: string, partnerData: any) {
      this.isLoggedIn = true;
      this.userType = 'contractor';
      this.partnerId = partnerId;
      this.partnerIdent = partnerData.partnerIdent;
      this.partnerName = partnerData.partnerName;
      this.partnerNip = partnerData.partnerNip;
      this.loginTimestamp = Date.now();
      this.saveToStorage();
    },

    selectPartner(partner: Partner) {
      this.selectedPartner = partner;
      this.saveToStorage();
    },

    clearSelectedPartner() {
      this.selectedPartner = null;
      this.saveToStorage();
    },

    logout(reason: SessionEndReason = 'logout') {
      // Zamknięcie wpisu w dzienniku — w tle, wylogowanie na nie nie czeka.
      // Numer pracownika podajemy jawnie: za chwilę migawka tożsamości zniknie.
      if (this.sessionId && this.employeeId) {
        void endSession(this.employeeId, this.sessionId, reason);
      }
      // KOLEJNOŚĆ MA ZNACZENIE: `state()` spread'uje `loadStateFromStorage()`, więc
      // `$reset()` odtwarza stan Z LOCALSTORAGE, a nie pusty. Wykonany przed
      // usunięciem klucza przywracał właśnie wylogowaną sesję — wylogowanie działało
      // dopiero po przeładowaniu strony. Na ścieżce 401 z whoami (revoke tokenu albo
      // token nieznany tej bramie) to znaczyło, że odwołanie sesji nie skutkowało.
      localStorage.removeItem(STORAGE_KEY);
      // Migawka ma zostać PUSTA. Samo usunięcie klucza nie wystarczy: gdyby
      // tożsamość wróciła do czytania localStorage, wciągnęłaby seed cudzej karty.
      identityService.clear();
      this.$reset();
    },

    isSessionExpired(): boolean {
      // 8h absolutne od zalogowania — jak token sesyjny bramy FConnect
      // (TOKEN_TTL_MS w GatewayServer.java); bez idle-timeoutu, bo kiosk
      // to narzedzie terenowe (przerwy miedzy wizytami > 1h sa normalne)
      const SESSION_TIMEOUT = 8 * 60 * 60 * 1000;
      const now = Date.now();
      return this.isLoggedIn && (now - this.loginTimestamp) > SESSION_TIMEOUT;
    },
  },
});


